import type {
  BehaviorConnection,
  BehaviorNode,
  Port,
  VariableRefNode,
} from "./types.js";
import type {
  BehaviorGraph,
  SceneVariableEntry,
  SceneVariableInputPort,
} from "../layout/types.js";
import type { PrimitiveValueType, VariableValueType } from "./types.js";
import { FUNCTION_PARAMETER_VALUE_TYPES } from "./function-graph.js";
import type {
  BehaviorGraphPayload,
  ExecutionContext,
  SceneVariableOverride,
} from "./runtime/types.js";
import { BehaviorRuntime } from "./runtime/BehaviorRuntime.js";
import { ensureBindingExpression } from "./runtime/helpers.js";
import { centerBehaviorGraphInViewport } from "./behavior-graph-layout.js";

/** Value input on `variableEnd` - the computed result of the variable body. */
export const VARIABLE_VALUE_PORT_ID = "value";

/** Output port on `variableRef` nodes. */
export const VARIABLE_REF_OUT_PORT_ID = "out";

/** Value types offered when adding a variable input port (no flow). */
export const VARIABLE_PARAMETER_VALUE_TYPES: readonly PrimitiveValueType[] =
  FUNCTION_PARAMETER_VALUE_TYPES;

/**
 * Builds output ports for a `variableBegin` node from catalog inputs.
 */
export function buildVariableBeginPorts(
  inputs: readonly SceneVariableInputPort[],
): Port[] {
  return inputs.map((entry) => ({
    id: entry.id,
    label: entry.label,
    direction: "output" as const,
    valueType: entry.valueType,
    ...(entry.objectTypeId ? { objectTypeId: entry.objectTypeId } : {}),
  }));
}

/**
 * Builds the single value input port on `variableEnd`.
 */
export function buildVariableEndPorts(
  outputValueType: VariableValueType,
  valuePortLabel: string,
  objectTypeId?: string,
): Port[] {
  return [
    {
      id: VARIABLE_VALUE_PORT_ID,
      label: valuePortLabel,
      direction: "input",
      valueType: outputValueType,
      ...(objectTypeId ? { objectTypeId } : {}),
    },
  ];
}

/**
 * Builds ports for a `variableRef` from a catalog entry.
 * Preserves literal values on input ports when `existingPorts` is provided.
 */
export function buildVariableRefPorts(
  entry: Pick<
    SceneVariableEntry,
    "inputs" | "outputValueType" | "outputObjectTypeId"
  >,
  outputLabel: string,
  existingPorts?: readonly Port[],
): Port[] {
  const preservedValue = (portId: string) =>
    existingPorts?.find((port) => port.id === portId)?.value;

  return [
    ...entry.inputs.map((input) => ({
      id: input.id,
      label: input.label,
      direction: "input" as const,
      valueType: input.valueType,
      ...(input.objectTypeId ? { objectTypeId: input.objectTypeId } : {}),
      value: preservedValue(input.id),
    })),
    {
      id: VARIABLE_REF_OUT_PORT_ID,
      label: outputLabel,
      direction: "output",
      valueType: entry.outputValueType,
      ...(entry.outputObjectTypeId
        ? { objectTypeId: entry.outputObjectTypeId }
        : {}),
    },
  ];
}

/**
 * Synchronizes `variableBegin` / `variableEnd` terminal ports with catalog I/O.
 */
export function syncVariableGraphTerminalPorts(
  graph: BehaviorGraph,
  entry: Pick<
    SceneVariableEntry,
    "inputs" | "outputValueType" | "outputObjectTypeId"
  >,
  labels: { valuePort: string; refOutput: string },
): BehaviorGraph {
  const nodes = graph.nodes.map((node) => {
    if (node.type === "control" && node.controlType === "variableBegin") {
      return {
        ...node,
        ports: buildVariableBeginPorts(entry.inputs),
      };
    }
    if (node.type === "control" && node.controlType === "variableEnd") {
      return {
        ...node,
        ports: buildVariableEndPorts(
          entry.outputValueType,
          labels.valuePort,
          entry.outputObjectTypeId,
        ),
      };
    }
    return node;
  });

  const portIdsByNodeId = new Map(
    nodes.map((node) => [node.id, new Set(node.ports.map((port) => port.id))]),
  );

  const connections = graph.connections.filter((connection) => {
    const fromPorts = portIdsByNodeId.get(connection.fromNodeId);
    const toPorts = portIdsByNodeId.get(connection.toNodeId);
    return (
      Boolean(fromPorts?.has(connection.fromPortId)) &&
      Boolean(toPorts?.has(connection.toPortId))
    );
  });

  return { nodes, connections };
}

/**
 * Rebuilds `variableRef` node ports from the variable catalog.
 */
export function syncVariableRefNodePortsInGraph(
  graph: Pick<BehaviorGraph, "nodes" | "connections">,
  variables: readonly Pick<
    SceneVariableEntry,
    "id" | "inputs" | "outputValueType" | "outputObjectTypeId"
  >[],
  labels: { refOutput: string },
  options?: { filterConnections?: boolean },
): BehaviorGraph {
  const nodes = graph.nodes.map((node) => {
    if (
      node.type !== "variable" ||
      node.valueType !== "variableRef" ||
      !("variableId" in node)
    ) {
      return node;
    }
    const entry = variables.find((item) => item.id === node.variableId);
    if (!entry) return node;
    return {
      ...node,
      ports: buildVariableRefPorts(entry, labels.refOutput, node.ports),
    };
  });

  if (options?.filterConnections === false) {
    return { nodes, connections: graph.connections };
  }

  const portIdsByNodeId = new Map(
    nodes.map((node) => [node.id, new Set(node.ports.map((port) => port.id))]),
  );

  const connections = graph.connections.filter((connection) => {
    const fromPorts = portIdsByNodeId.get(connection.fromNodeId);
    const toPorts = portIdsByNodeId.get(connection.toNodeId);
    return (
      Boolean(fromPorts?.has(connection.fromPortId)) &&
      Boolean(toPorts?.has(connection.toPortId))
    );
  });

  return { nodes, connections };
}

export function variableGraphHasVariableBegin(
  graph: Pick<BehaviorGraph, "nodes"> | undefined,
): boolean {
  if (!graph) return false;
  return graph.nodes.some(
    (node) => node.type === "control" && node.controlType === "variableBegin",
  );
}

export function variableGraphHasVariableEnd(
  graph: Pick<BehaviorGraph, "nodes"> | undefined,
): boolean {
  if (!graph) return false;
  return graph.nodes.some(
    (node) => node.type === "control" && node.controlType === "variableEnd",
  );
}

/**
 * A scene variable is publishable when its graph contains exactly one
 * `variableEnd` and at most one `variableBegin` (begin is optional).
 */
export function isVariableGraphPublishable(
  graph: Pick<BehaviorGraph, "nodes"> | undefined,
): boolean {
  if (!graph) return false;
  const beginCount = graph.nodes.filter(
    (node) => node.type === "control" && node.controlType === "variableBegin",
  ).length;
  const endCount = graph.nodes.filter(
    (node) => node.type === "control" && node.controlType === "variableEnd",
  ).length;
  return beginCount <= 1 && endCount === 1;
}

/** Maximum nested `variableRef` depth (cycle / stack guard). */
export const MAX_VARIABLE_EVAL_DEPTH = 32;

export interface VariableEvalFrame {
  variableId: string;
  refNodeId: string;
}

export function isVariableEvalBlocked(
  evalStack: readonly VariableEvalFrame[] | undefined,
  variableId: string,
): boolean {
  const stack = evalStack ?? [];
  if (stack.some((frame) => frame.variableId === variableId)) {
    return true;
  }
  return stack.length >= MAX_VARIABLE_EVAL_DEPTH;
}

/**
 * Default graph for a new scene variable: `variableBegin` left, `variableEnd` right.
 */
export function createDefaultVariableGraph(options: {
  canvasWidth: number;
  canvasHeight: number;
  beginNodeId?: string;
  endNodeId?: string;
  valuePortLabel?: string;
}): BehaviorGraph {
  const canvasWidth = Math.max(1, options.canvasWidth);
  const canvasHeight = Math.max(1, options.canvasHeight);
  const beginNodeId = options.beginNodeId ?? crypto.randomUUID();
  const endNodeId = options.endNodeId ?? crypto.randomUUID();
  const y = Math.max(0, Math.round(canvasHeight / 2 - 50));
  const beginX = 40;
  const endX = Math.max(beginX + 240, canvasWidth - 320);
  const valuePortLabel = options.valuePortLabel ?? "";

  const variableBeginNode: BehaviorNode = {
    id: beginNodeId,
    x: beginX,
    y,
    type: "control",
    controlType: "variableBegin",
    dataValueType: "string",
    ports: buildVariableBeginPorts([]),
  };

  const variableEndNode: BehaviorNode = {
    id: endNodeId,
    x: endX,
    y,
    type: "control",
    controlType: "variableEnd",
    dataValueType: "string",
    ports: buildVariableEndPorts("string", valuePortLabel),
  };

  return {
    nodes: [variableBeginNode, variableEndNode],
    connections: [],
  };
}

// ── setVariable helpers ─────────────────────────────────────────────────────

/** Port IDs on `setVariable` action nodes. */
export const SET_VARIABLE_FLOW_IN_PORT_ID = "in";
export const SET_VARIABLE_VALUE_PORT_ID = "value";
export const SET_VARIABLE_FLOW_OUT_PORT_ID = "out";

/**
 * Builds ports for a `setVariable` action from a variable catalog entry.
 * Preserves literal values on the `value` input port from `existingPorts`.
 */
export function buildSetVariablePorts(
  entry: Pick<
    SceneVariableEntry,
    "inputs" | "outputValueType" | "outputObjectTypeId"
  >,
  labels: { flowIn: string; flowOut: string; value: string },
  existingPorts?: readonly Port[],
): Port[] {
  const preservedValue = (portId: string) =>
    existingPorts?.find((port) => port.id === portId)?.value;

  return [
    {
      id: SET_VARIABLE_FLOW_IN_PORT_ID,
      label: labels.flowIn,
      direction: "input",
      valueType: "flow",
    },
    {
      id: SET_VARIABLE_VALUE_PORT_ID,
      label: labels.value,
      direction: "input",
      valueType: entry.outputValueType,
      ...(entry.outputObjectTypeId
        ? { objectTypeId: entry.outputObjectTypeId }
        : {}),
      value: preservedValue(SET_VARIABLE_VALUE_PORT_ID),
    },
    {
      id: SET_VARIABLE_FLOW_OUT_PORT_ID,
      label: labels.flowOut,
      direction: "output",
      valueType: "flow",
    },
  ];
}

/**
 * Rebuilds `setVariable` node ports from the variable catalog and optionally
 * drops connections that reference removed ports.
 */
export function syncSetVariableNodePortsInGraph(
  graph: Pick<BehaviorGraph, "nodes" | "connections">,
  variables: readonly Pick<
    SceneVariableEntry,
    "id" | "inputs" | "outputValueType" | "outputObjectTypeId"
  >[],
  labels: { flowIn: string; flowOut: string; value: string },
  options?: { filterConnections?: boolean },
): BehaviorGraph {
  const nodes = graph.nodes.map((node) => {
    if (
      node.type !== "action" ||
      node.kind !== "setVariable" ||
      !("variableId" in node)
    ) {
      return node;
    }
    const entry = variables.find((item) => item.id === node.variableId);
    if (!entry) return node;
    return {
      ...node,
      ports: buildSetVariablePorts(entry, labels, node.ports),
    };
  });

  if (options?.filterConnections === false) {
    return { nodes, connections: graph.connections };
  }

  const portIdsByNodeId = new Map(
    nodes.map((n) => [n.id, new Set(n.ports.map((p) => p.id))]),
  );

  const connections = graph.connections.filter((c) => {
    const fromPorts = portIdsByNodeId.get(c.fromNodeId);
    const toPorts = portIdsByNodeId.get(c.toNodeId);
    return (
      Boolean(fromPorts?.has(c.fromPortId)) && Boolean(toPorts?.has(c.toPortId))
    );
  });

  return { nodes, connections };
}

/**
 * Extracts the upstream subgraph that feeds a specific input port of a node
 * in a parent runtime. Walks incoming edges recursively and collects all
 * nodes + connections that contribute to the value.
 */
export function extractUpstreamValueSubgraph(
  parentRuntime: BehaviorRuntime,
  setVariableNodeId: string,
  valuePortId: string,
): SceneVariableOverride | undefined {
  const setVarNode = parentRuntime.getNode(setVariableNodeId);
  if (!setVarNode) return undefined;

  const incomingEdges = setVarNode.incomingEdgesByPort.get(valuePortId);
  if (!incomingEdges || incomingEdges.length === 0) return undefined;

  const edge = incomingEdges[0]!;
  const collectedNodeIds = new Set<string>();
  const collectedConnectionKeys = new Set<string>();
  const resultNodes: BehaviorNode[] = [];
  const resultConnections: BehaviorGraphPayload["connections"] = [];

  const queue = [edge.fromNode];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (collectedNodeIds.has(current.behavior.id)) continue;
    collectedNodeIds.add(current.behavior.id);
    resultNodes.push(current.behavior);

    for (const [, edges] of current.incomingEdgesByPort) {
      for (const incoming of edges) {
        const connKey = `${incoming.fromNode.behavior.id}:${incoming.fromPort}->${current.behavior.id}:${incoming.port}`;
        if (!collectedConnectionKeys.has(connKey)) {
          collectedConnectionKeys.add(connKey);
          resultConnections.push({
            id: connKey,
            fromNodeId: incoming.fromNode.behavior.id,
            fromPortId: incoming.fromPort,
            toNodeId: current.behavior.id,
            toPortId: incoming.port,
          });
        }
        if (!collectedNodeIds.has(incoming.fromNode.behavior.id)) {
          queue.push(incoming.fromNode);
        }
      }
    }
  }

  return {
    sourceGraph: { nodes: resultNodes, connections: resultConnections },
    sinkNodeId: edge.fromNode.behavior.id,
    sinkPortId: edge.fromPort,
  };
}

/**
 * Evaluates a stored variable override by building a fresh runtime from
 * the captured subgraph and reading the sink node's output.
 */
export function evaluateVariableOverride(
  override: SceneVariableOverride,
  context: ExecutionContext,
  binding: boolean,
): unknown {
  const runtime = BehaviorRuntime.build(override.sourceGraph);
  if (binding) {
    const variableEnd = [...runtime.nodes.values()].find(
      (node) =>
        node.behavior.type === "control" &&
        node.behavior.controlType === "variableEnd",
    );
    if (variableEnd) {
      return ensureBindingExpression(
        variableEnd.getInput(
          VARIABLE_VALUE_PORT_ID,
          new Set<string>(),
          context,
          true,
        ),
      );
    }
  }
  const sinkNode = runtime.getNode(override.sinkNodeId);
  if (!sinkNode) return undefined;
  const raw = sinkNode.getOutput(
    override.sinkPortId,
    new Set<string>(),
    context,
    binding,
  );
  return binding ? ensureBindingExpression(raw) : raw;
}

// ── Extract selection to scene variable ─────────────────────────────────────

/** Describes the single value output that leaves a multi-node selection. */
export interface SelectionVariableExit {
  nodeId: string;
  portId: string;
  outputValueType: VariableValueType;
}

function toVariableOutputValueType(
  valueType: string,
): VariableValueType | null {
  if (valueType === "flow" || valueType === "binding") {
    return null;
  }
  return valueType as VariableValueType;
}

/**
 * Finds value output ports on selected nodes that are not wired to another
 * selected node (the subgraph's external value exits).
 */
export function findSelectionExternalValueOutputs(
  graph: Pick<BehaviorGraph, "nodes" | "connections">,
  selectedNodeIds: readonly string[],
): SelectionVariableExit[] {
  const selectedIds = new Set(selectedNodeIds);
  if (selectedIds.size === 0) return [];

  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));
  const exits: SelectionVariableExit[] = [];

  for (const nodeId of selectedIds) {
    const node = nodesById.get(nodeId);
    if (!node) continue;

    for (const port of node.ports) {
      if (port.direction !== "output" || port.valueType === "flow") {
        continue;
      }
      const wiredInsideSelection = graph.connections.some(
        (connection) =>
          connection.fromNodeId === nodeId &&
          connection.fromPortId === port.id &&
          selectedIds.has(connection.toNodeId),
      );
      if (wiredInsideSelection) continue;

      const outputValueType = toVariableOutputValueType(port.valueType);
      if (!outputValueType) continue;

      exits.push({
        nodeId,
        portId: port.id,
        outputValueType,
      });
    }
  }

  return exits;
}

/**
 * Returns whether a selection can be promoted to a scene variable:
 * at least two nodes, no flow wires crossing the boundary, exactly one value exit.
 */
export function analyzeSelectionForVariableExtraction(
  graph: Pick<BehaviorGraph, "nodes" | "connections">,
  selectedNodeIds: readonly string[],
): SelectionVariableExit | null {
  if (selectedNodeIds.length < 2) return null;

  const selectedIds = new Set(selectedNodeIds);
  const nodesById = new Map(graph.nodes.map((node) => [node.id, node]));

  for (const nodeId of selectedIds) {
    if (!nodesById.has(nodeId)) return null;
  }

  for (const connection of graph.connections) {
    const fromInside = selectedIds.has(connection.fromNodeId);
    const toInside = selectedIds.has(connection.toNodeId);
    if (fromInside === toInside) continue;

    const fromNode = nodesById.get(connection.fromNodeId);
    const toNode = nodesById.get(connection.toNodeId);
    const fromPort = fromNode?.ports.find(
      (port) => port.id === connection.fromPortId,
    );
    const toPort = toNode?.ports.find(
      (port) => port.id === connection.toPortId,
    );
    if (fromPort?.valueType === "flow" || toPort?.valueType === "flow") {
      return null;
    }
  }

  const exits = findSelectionExternalValueOutputs(graph, selectedNodeIds);
  if (exits.length !== 1) return null;
  return exits[0] ?? null;
}

export interface VariableExtractionFromSelectionOptions {
  variableId: string;
  variableName: string;
  valuePortLabel: string;
  refOutputLabel: string;
  /** Visible behavior canvas width in world coordinates (zoom = 1). */
  canvasWidth: number;
  /** Visible behavior canvas height in world coordinates (zoom = 1). */
  canvasHeight: number;
}

export interface VariableExtractionFromSelectionResult {
  catalogEntry: SceneVariableEntry;
  parentGraph: BehaviorGraph;
}

/**
 * Promotes a behavior subgraph selection into a new scene variable catalog entry
 * and replaces the selection with a `variableRef` in the parent graph.
 */
export function extractBehaviorSelectionToSceneVariable(
  parentGraph: BehaviorGraph,
  selectedNodeIds: readonly string[],
  options: VariableExtractionFromSelectionOptions,
): VariableExtractionFromSelectionResult | null {
  const exit = analyzeSelectionForVariableExtraction(
    parentGraph,
    selectedNodeIds,
  );
  if (!exit) return null;

  const selectedIds = new Set(selectedNodeIds);
  const nodesById = new Map(parentGraph.nodes.map((node) => [node.id, node]));

  const externalInputsByTargetKey = new Map<
    string,
    {
      targetNodeId: string;
      targetPortId: string;
      valueType: VariableValueType;
      label: string;
    }
  >();

  for (const connection of parentGraph.connections) {
    if (selectedIds.has(connection.fromNodeId)) continue;
    if (!selectedIds.has(connection.toNodeId)) continue;

    const toNode = nodesById.get(connection.toNodeId);
    const toPort = toNode?.ports.find(
      (port) => port.id === connection.toPortId,
    );
    if (
      !toPort ||
      toPort.direction !== "input" ||
      toPort.valueType === "flow"
    ) {
      continue;
    }

    const valueType = toVariableOutputValueType(toPort.valueType);
    if (!valueType) continue;

    const key = `${connection.toNodeId}:${connection.toPortId}`;
    if (!externalInputsByTargetKey.has(key)) {
      externalInputsByTargetKey.set(key, {
        targetNodeId: connection.toNodeId,
        targetPortId: connection.toPortId,
        valueType,
        label: toPort.label,
      });
    }
  }

  const inputs: SceneVariableInputPort[] = [];
  const catalogInputIdByTargetKey = new Map<string, string>();

  for (const [key, external] of externalInputsByTargetKey) {
    const inputId = crypto.randomUUID();
    catalogInputIdByTargetKey.set(key, inputId);
    inputs.push({
      id: inputId,
      label: external.label,
      valueType: external.valueType,
    });
  }

  const idMap = new Map<string, string>();
  for (const nodeId of selectedIds) {
    idMap.set(nodeId, crypto.randomUUID());
  }

  const selectedNodes = parentGraph.nodes.filter((node) =>
    selectedIds.has(node.id),
  );
  const minX = Math.min(...selectedNodes.map((node) => node.x));
  const minY = Math.min(...selectedNodes.map((node) => node.y));
  const maxX = Math.max(...selectedNodes.map((node) => node.x));

  const beginNodeId = crypto.randomUUID();
  const endNodeId = crypto.randomUUID();
  const beginX = 40;
  const bodyOffsetX = beginX + 220 - minX;
  const bodyOffsetY = Math.max(0, Math.round(200 - minY));
  const endX = Math.max(beginX + 280, maxX + bodyOffsetX + 120);

  const clonedNodes: BehaviorNode[] = selectedNodes.map((node) => ({
    ...structuredClone(node),
    id: idMap.get(node.id) ?? node.id,
    x: node.x + bodyOffsetX,
    y: node.y + bodyOffsetY,
    ports: node.ports.map((port) => ({ ...port })),
  }));

  const bodyConnections: BehaviorConnection[] = parentGraph.connections
    .filter(
      (connection) =>
        selectedIds.has(connection.fromNodeId) &&
        selectedIds.has(connection.toNodeId),
    )
    .map((connection) => ({
      id: crypto.randomUUID(),
      fromNodeId: idMap.get(connection.fromNodeId) ?? connection.fromNodeId,
      fromPortId: connection.fromPortId,
      toNodeId: idMap.get(connection.toNodeId) ?? connection.toNodeId,
      toPortId: connection.toPortId,
    }));

  const anchorY = Math.min(...clonedNodes.map((node) => node.y));

  const variableBeginNode: BehaviorNode = {
    id: beginNodeId,
    x: beginX,
    y: anchorY,
    type: "control",
    controlType: "variableBegin",
    dataValueType: exit.outputValueType,
    ports: buildVariableBeginPorts(inputs),
  };

  const variableEndNode: BehaviorNode = {
    id: endNodeId,
    x: endX,
    y: anchorY,
    type: "control",
    controlType: "variableEnd",
    dataValueType: exit.outputValueType,
    ports: buildVariableEndPorts(exit.outputValueType, options.valuePortLabel),
  };

  const remappedExitNodeId = idMap.get(exit.nodeId);
  if (remappedExitNodeId) {
    bodyConnections.push({
      id: crypto.randomUUID(),
      fromNodeId: remappedExitNodeId,
      fromPortId: exit.portId,
      toNodeId: endNodeId,
      toPortId: VARIABLE_VALUE_PORT_ID,
    });
  }

  for (const [key, external] of externalInputsByTargetKey) {
    const inputPortId = catalogInputIdByTargetKey.get(key);
    const targetNodeId = idMap.get(external.targetNodeId);
    if (!inputPortId || !targetNodeId) continue;
    bodyConnections.push({
      id: crypto.randomUUID(),
      fromNodeId: beginNodeId,
      fromPortId: inputPortId,
      toNodeId: targetNodeId,
      toPortId: external.targetPortId,
    });
  }

  const variableGraph: BehaviorGraph = {
    nodes: [variableBeginNode, ...clonedNodes, variableEndNode],
    connections: bodyConnections,
  };

  const syncedVariableGraph = syncVariableGraphTerminalPorts(
    variableGraph,
    { inputs, outputValueType: exit.outputValueType },
    {
      valuePort: options.valuePortLabel,
      refOutput: options.refOutputLabel,
    },
  );

  const centeredVariableGraph = centerBehaviorGraphInViewport(
    syncedVariableGraph,
    options.canvasWidth,
    options.canvasHeight,
  );

  const catalogEntry: SceneVariableEntry = {
    id: options.variableId,
    name: options.variableName,
    outputValueType: exit.outputValueType,
    inputs,
    graph: centeredVariableGraph,
  };

  const centerX =
    selectedNodes.reduce((sum, node) => sum + node.x, 0) / selectedNodes.length;
  const centerY =
    selectedNodes.reduce((sum, node) => sum + node.y, 0) / selectedNodes.length;

  const variableRefNode: VariableRefNode = {
    id: crypto.randomUUID(),
    x: centerX,
    y: centerY,
    type: "variable",
    valueType: "variableRef",
    variableId: options.variableId,
    ports: buildVariableRefPorts(catalogEntry, options.refOutputLabel),
  };

  const remainingNodes = parentGraph.nodes.filter(
    (node) => !selectedIds.has(node.id),
  );
  const remainingConnections = parentGraph.connections.filter(
    (connection) =>
      !selectedIds.has(connection.fromNodeId) &&
      !selectedIds.has(connection.toNodeId),
  );

  const rewiredConnections: BehaviorConnection[] = [...remainingConnections];

  for (const connection of parentGraph.connections) {
    if (selectedIds.has(connection.fromNodeId)) continue;
    if (!selectedIds.has(connection.toNodeId)) continue;

    const inputPortId = catalogInputIdByTargetKey.get(
      `${connection.toNodeId}:${connection.toPortId}`,
    );
    if (!inputPortId) continue;

    rewiredConnections.push({
      id: crypto.randomUUID(),
      fromNodeId: connection.fromNodeId,
      fromPortId: connection.fromPortId,
      toNodeId: variableRefNode.id,
      toPortId: inputPortId,
    });
  }

  for (const connection of parentGraph.connections) {
    if (!selectedIds.has(connection.fromNodeId)) continue;
    if (selectedIds.has(connection.toNodeId)) continue;

    if (
      connection.fromNodeId !== exit.nodeId ||
      connection.fromPortId !== exit.portId
    ) {
      continue;
    }

    rewiredConnections.push({
      id: crypto.randomUUID(),
      fromNodeId: variableRefNode.id,
      fromPortId: VARIABLE_REF_OUT_PORT_ID,
      toNodeId: connection.toNodeId,
      toPortId: connection.toPortId,
    });
  }

  return {
    catalogEntry,
    parentGraph: {
      nodes: [...remainingNodes, variableRefNode],
      connections: rewiredConnections,
    },
  };
}
